import { useState } from 'react';
import { ArrowLeft, GitFork, Plus } from 'lucide-react';
import type { RubricListItemDto } from '@sara/api-client';
import { Badge } from '@sara/ui/components/badge';
import { Button } from '@sara/ui/components/button';
import { Skeleton } from '@sara/ui/components/skeleton';

interface CreateRubricOptionsProps {
  /** undefined while the rubric list is loading */
  rubrics: RubricListItemDto[] | undefined;
  isError?: boolean;
  onFork: (rubricId: string) => void;
  onStartBlank: () => void;
}

function OptionCard({
  icon: Icon,
  title,
  description,
  disabled,
  disabledHint,
  onClick,
  testId,
}: {
  icon: typeof GitFork;
  title: string;
  description: string;
  disabled?: boolean;
  disabledHint?: string;
  onClick: () => void;
  testId: string;
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      disabled={disabled}
      className="group flex flex-col items-start gap-2 rounded-lg border border-border/70 bg-card p-4 text-left transition-colors hover:border-border hover:bg-secondary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:border-border/70 disabled:hover:bg-card"
    >
      <span className="flex items-center gap-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-md border border-border/70 bg-background text-foreground/70">
          <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />
        </span>
        <span className="text-[14px] font-semibold leading-tight text-foreground">{title}</span>
      </span>
      <span className="text-[12.5px] leading-snug text-muted-foreground">
        {disabled && disabledHint ? disabledHint : description}
      </span>
    </button>
  );
}

export function CreateRubricOptions({
  rubrics,
  isError,
  onFork,
  onStartBlank,
}: CreateRubricOptionsProps) {
  const [step, setStep] = useState<'options' | 'pick'>('options');

  if (step === 'pick') {
    return (
      <div className="space-y-3">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setStep('options')}
          className="h-7 gap-1.5 px-2 text-xs text-muted-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.75} />
          Back
        </Button>

        {isError ? (
          <p className="rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-3 text-[12.5px] text-destructive">
            Couldn&apos;t load your rubrics — close the dialog and try again.
          </p>
        ) : rubrics === undefined ? (
          <div className="space-y-2">
            <Skeleton className="h-14 rounded-lg" />
            <Skeleton className="h-14 rounded-lg" />
            <Skeleton className="h-14 rounded-lg" />
          </div>
        ) : rubrics.length === 0 ? (
          <p className="px-1 py-2 text-[12.5px] text-muted-foreground">
            No rubrics to fork yet — start blank instead.
          </p>
        ) : (
          <ul className="max-h-72 space-y-2 overflow-y-auto pr-1" data-testid="fork-source-list">
            {rubrics.map((rubric) => (
              <li key={rubric.id}>
                <button
                  type="button"
                  onClick={() => onFork(rubric.id)}
                  className="group flex w-full flex-col gap-0.5 rounded-lg border border-border/70 bg-card px-4 py-3 text-left transition-colors hover:border-border hover:bg-secondary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <span className="flex items-baseline gap-2.5">
                    <span className="text-[14px] font-medium tracking-tight text-foreground">
                      {rubric.name}
                    </span>
                    <span className="text-[11px] font-medium tabular-nums text-muted-foreground">
                      v{rubric.latestVersion}
                    </span>
                    <span className="text-[11px] tabular-nums text-muted-foreground">
                      {rubric.dimensionCount}{' '}
                      {rubric.dimensionCount === 1 ? 'dimension' : 'dimensions'}
                    </span>
                    {rubric.archivedAt ? (
                      <Badge variant="outline" className="text-[10px]">
                        Archived
                      </Badge>
                    ) : null}
                  </span>
                  {rubric.description && (
                    <span className="line-clamp-1 text-[12.5px] text-muted-foreground">
                      {rubric.description}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  const noRubrics = rubrics !== undefined && rubrics.length === 0;

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <OptionCard
        icon={GitFork}
        title="Fork an existing rubric"
        description="Copy any rubric's latest version and adjust the parts you need. Fully editable."
        disabled={noRubrics}
        disabledHint="You don't have any rubrics to fork yet."
        onClick={() => setStep('pick')}
        testId="fork-existing-rubric-option"
      />
      <OptionCard
        icon={Plus}
        title="Start blank"
        description="A clean slate. Define dimensions and score bands yourself."
        onClick={onStartBlank}
        testId="start-blank-option"
      />
    </div>
  );
}
